import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';

import { onEngagementIdChange, initialize as initJA, page } from '@jerry-serverless/analyticslib/frontend';

export const JA_SESSION_ID_COOKIE_NAME = 'jasid';

export const JERRY_ANALYTICS_DEVICE_ID_COOKIE_NAME = 'jadid';

export const JERRY_ANALYTICS_ENGAGEMENT_ID_MAX_EXPIRED_TIME = 10 * 60 * 1000;
export const JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME = 'engagement_id';

export interface JerryAnalyticsScriptProps {
  jaURL?: string;
  releaseEnv?: string;
  pageEvent?: boolean; // Whether to send page events before flushing the event queue, Defaults to true.
  metadata?: Record<string, unknown>;
}

/**
 * Init session id
 * Before request to /graphql, you need to invoke this, or graphql will set cookie, will cause inconsistent of session id
 * @return sessionId
 */
export const initSessionId = (): string => {
  let sessionID: string | undefined = Cookies.get(JA_SESSION_ID_COOKIE_NAME) || window['jasid'];
  if (!sessionID) {
    sessionID = uuid();
    Cookies.set(JA_SESSION_ID_COOKIE_NAME, sessionID);
  }
  return sessionID;
};

/**
 * Init engagement id
 */
export const initEngagementId = (): string => {
  let engagementID: string | undefined = Cookies.get(JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME);
  if (!engagementID) {
    engagementID = uuid();
    Cookies.set(JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME, engagementID);
  }
  return engagementID;
};

/**
 * @see https://www.notion.so/jerrydesign/Jerry-Analytics-Session-Spec-4a5cd0d8b07c4ff4a2b6d3073890160b
 * Engagement id guard:
 * The webpage is out of focus for more than 10 minutes.
 * We record the time when we last webpage lost focus, and compare that time when we regain focus.
 * If it takes more than 10 minutes, a new engagement_id will be generated.
 */
export const engagementIdGuard = (): void | (() => void) => {
  if (window['Jerry']?.mobileAppView) {
    /**
     * engagement_id will be taken over by the app if it is in the WebView
     */
    return;
  }
  /**
   * The JERRY_BLUR_TICK is saved in sessionStorage to handle multiple pages opened at the same time
   */
  const BLUR_TICK_NAME = 'jerry/analytics/engagementID/blurTick';
  const visibilityChanged = () => {
    const currentTime = new Date().getTime();
    if (document.visibilityState === 'visible') {
      const lastTime = Number(window.sessionStorage.getItem(BLUR_TICK_NAME) ?? 0);
      if (!isNaN(lastTime) && currentTime - lastTime >= JERRY_ANALYTICS_ENGAGEMENT_ID_MAX_EXPIRED_TIME) {
        const newEngagementID = uuid();
        onEngagementIdChange(newEngagementID);
        Cookies.set(JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME, newEngagementID);
      }
      window.sessionStorage.removeItem(BLUR_TICK_NAME);
    } else {
      window.sessionStorage.setItem(BLUR_TICK_NAME, String(currentTime));
    }
  };

  document.addEventListener('visibilitychange', visibilityChanged);
  return () => document.removeEventListener('visibilitychange', visibilityChanged);
};

export const JerryAnalyticsScript: FunctionComponent<JerryAnalyticsScriptProps> = (props) => {
  const router = useRouter();

  useEffect(() => {
    const engagementID = initEngagementId();
    const sessionID: string | undefined = initSessionId();
    window['jasid'] = sessionID;

    // Device ID only exist when signup in jerry app (set by app)
    const deviceID: string | undefined = Cookies.get(JERRY_ANALYTICS_DEVICE_ID_COOKIE_NAME);

    void initJA(
      props.jaURL || process.env.NEXT_PUBLIC_JA_URL || '',
      pickBy(
        {
          releaseEnv: props.releaseEnv || process.env.NEXT_PUBLIC_STAGE || 'dev',
          sessionID,
          deviceID,
          engagementID,
          metadata: props.metadata,
        },
        identity
      ),
      props.pageEvent
    );

    // Register route change listener to send `page` events on each route change.
    const trackRouteChange = () => {
      void page();
    };
    const engagementIDGuardSub = engagementIdGuard();
    router.events.on('routeChangeComplete', trackRouteChange);
    return () => {
      engagementIDGuardSub?.();
      router.events.off('routeChangeComplete', trackRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default JerryAnalyticsScript;
